import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  Input,
  CardHeader,
  CardFooter,
  Button,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
  getAccount as onGetAccount,
  updateAccount as onUpdateAccount,
} from "../../slices/accounts/thunk";
import { clearError, clearAccount } from "../../slices/accounts/reducer";
import { getPhases } from "../../slices/phases/thunk";
import { useProfile } from "../../Components/Hooks/UserHooks";
import { useParams, useNavigate } from "react-router-dom";

const selectAccountState = createSelector(
  (state) => state.Accounts,
  (accounts) => ({
    account: accounts.account || null,
    isAccountSuccess: accounts.isAccountSuccess,
    isAccountFetched: accounts.isAccountFetched,
    isLoading: accounts.isLoading,
    error: accounts.error,
  })
);

const selectPhaseState = createSelector(
  (state) => state.Phases,
  (phase) => ({
    phases: phase.phases || [],
    isPhaseSuccess: phase.isPhaseSuccess || false,
    phaseError: phase.error,
  })
);

const AccountDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { account, isAccountSuccess, isAccountFetched, isLoading, error } =
    useSelector(selectAccountState);
  const { phases, isPhaseSuccess, phaseError } = useSelector(selectPhaseState);
  const { userProfile, loading: profileLoading } = useProfile();
  const { id } = useParams();

  const isViewer = useMemo(() => {
    return userProfile?.user?.groups?.includes("Viewer") || false;
  }, [userProfile]);

  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(onGetAccount(id));
    }

    return () => {
      dispatch(clearAccount());
      dispatch(clearError());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (account) {
      setFormData(account);
    }
  }, [account]);

  useEffect(() => {
    if (phases.length === 0) {
      dispatch(getPhases());
    }
  }, [phases.length, dispatch]);

  useEffect(() => {
    if (error) {
      if (error.includes("not found") || error.includes("404")) {
        setTimeout(() => {
          navigate("/admin/panel/accounts");
        }, 2000);
      }
    }
  }, [error, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!formData?.id) {
      toast.error("Account ID is required for update");
      return;
    }

    if (!formData.cif || !formData.email) {
      toast.error("CIF and Email are required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData?.series) {
      toast.error("Account Series is required for update");
      return;
    }

    try {
      dispatch(clearError());

      await dispatch(
        onUpdateAccount({
          id: formData.id,
          ...formData,
        })
      ).unwrap();

      setIsEditing(false);
    } catch (error) {
      console.error("Save failed:", error);
      if (account) {
        setFormData(account);
      }
    }
  };

  const toggleEdit = () => {
    if (isEditing) {
      setFormData(account);
    }
    setIsEditing(!isEditing);
  };

  if (isLoading && !account) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!isLoading && !account && error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center flex-column"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <i className="ri-account-circle-line display-4 text-muted mb-3"></i>
              <h4>Account Not Found</h4>
              <p className="text-muted">
                The account you're looking for doesn't exist or has been
                removed.
              </p>
              <Button
                color="primary"
                onClick={() => navigate("/admin/panel/accounts")}
              >
                Back to Accounts
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                Loading account details...
              </span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "No data";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  };

  const maskToken = (token) => {
    if (!token) return "No token";
    if (token.length <= 8) return token;
    return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <CardHeader className="border-0 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">Account Details</h5>
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => navigate("/admin/panel/accounts")}
                  >
                    <i className="ri-arrow-left-line me-1"></i>
                    Back to Accounts
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="cif">
                        CIF <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="cif"
                        name="cif"
                        value={formData?.cif || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter CIF"
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="email">
                        Email <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData?.email || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter email address"
                      />
                    </div>
                  </Col>
                  
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="phase" className="form-label">
                        Phase
                      </Label>
                      <Input
                        type="select"
                        id="phase"
                        name="phase"
                        value={formData.phase}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Phase (Optional)</option>
                        {isPhaseSuccess && phases.length > 0 ? (
                          phases.map((phase) => (
                            <option key={phase.id} value={phase.id}>
                              {phase.name}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            {phaseError
                              ? "Error loading phases"
                              : "Loading phases..."}
                          </option>
                        )}
                      </Input>
                      <div className="form-text">
                        {phaseError ? (
                          <span className="text-danger">
                            Error loading phases. You can create the account and
                            set phase later.
                          </span>
                        ) : (
                          "Phase can be left empty and set later."
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="series">Series</Label>
                      <Input
                        type="text"
                        id="series"
                        name="series"
                        value={formData?.series || ""}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter series"
                      />
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="mb-3">
                      <Label for="token">Token</Label>
                      <Input
                        type="text"
                        id="token"
                        name="token"
                        value={
                          isEditing
                            ? formData?.token || ""
                            : maskToken(formData?.token)
                        }
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter token"
                      />
                      {!isEditing && formData?.token && (
                        <div className="form-text">
                          Token is masked for security. Click edit to
                          view/modify.
                        </div>
                      )}
                    </div>
                  </Col>
                  
                  {/* <Col md={6}>
                    <div className="mb-3">
                      <Label for="created_at">Created At</Label>
                      <Input
                        type="text"
                        id="created_at"
                        value={formatDate(formData?.created_at)}
                        disabled
                        className="bg-light"
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label for="updated_at">Updated At</Label>
                      <Input
                        type="text"
                        id="updated_at"
                        value={formatDate(formData?.updated_at)}
                        disabled
                        className="bg-light"
                      />
                    </div>
                  </Col> */}
                </Row>
              </CardBody>
              <CardFooter className="d-flex justify-content-between align-items-center">
                {!isViewer && (
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={toggleEdit}
                      disabled={isLoading}
                    >
                      {isEditing ? "Cancel Edit" : "Edit"}
                    </Button>
                    {isEditing && (
                      <Button
                        color="success"
                        onClick={handleSave}
                        disabled={isLoading}
                      >
                        {isLoading ? "Saving..." : "Save"}
                      </Button>
                    )}
                  </div>
                )}
                <div className="text-muted small">
                  Last updated: {formatDate(formData?.updated_at)}
                </div>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountDetails;
